import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { PostResponse } from '../models/posts/PostResponse'
import PostService from '../services/PostsService'

const PostDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [post, setPost] = useState<PostResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string>('')

  useEffect(() => {
    const fetchPost = async () => {
      try {
        if (id) {
          const response = await PostService.getPostById(Number(id))
          setPost(response.data)
          console.log('Post data:', response.data)
          if (response.data.image_url) {
            console.log(
              'Image URL:',
              `${process.env.REACT_APP_SERVER_URL || 'http://localhost:3000'}/${
                response.data.image_url
              }`,
            )
          }
        }
      } catch (error: any) {
        setError(error.response?.data?.message || 'Ошибка загрузки поста')
        console.error('Ошибка при получении поста:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchPost()
  }, [id])

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('ru-RU', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-600 mb-4">Ошибка</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/home')}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors"
          >
            Вернуться к постам
          </button>
        </div>
      </div>
    )
  }

  if (!post) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-800 mb-4">
            Пост не найден
          </h1>
          <button
            onClick={() => navigate('/home')}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors"
          >
            Вернуться к постам
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 py-6 sm:py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Navigation */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center text-blue-600 hover:text-blue-800 transition-colors mb-6"
        >
          <svg
            className="w-5 h-5 mr-2"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
          Назад
        </button>

        {/* Post Content */}
        <article className="bg-white rounded-lg shadow-lg overflow-hidden">
          {/* Post Image */}
          {post.image_url && (
            <div className="w-full h-64 sm:h-80 md:h-96 overflow-hidden">
              <img
                src={`${
                  process.env.REACT_APP_SERVER_URL || 'http://localhost:3000'
                }/${post.image_url}`}
                alt={post.title}
                className="w-full h-full object-cover"
                loading="lazy"
                onError={e => {
                  console.error(
                    'Ошибка загрузки изображения:',
                    `${
                      process.env.REACT_APP_SERVER_URL ||
                      'http://localhost:3000'
                    }/${post.image_url}`,
                  )
                  e.currentTarget.style.display = 'none'
                }}
              />
            </div>
          )}

          <div className="p-6 sm:p-8 lg:p-12">
            {/* Post Header */}
            <header className="mb-8">
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-4 leading-tight">
                {post.title}
              </h1>

              <div className="flex flex-wrap items-center text-sm text-gray-600 gap-4">
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center mr-3">
                    <span className="text-white font-medium">
                      {post.auth?.username?.charAt(0).toUpperCase() || 'A'}
                    </span>
                  </div>
                  <span className="font-medium">
                    {post.auth?.username || 'Анонимный автор'}
                  </span>
                </div>

                <div className="flex items-center">
                  <svg
                    className="w-4 h-4 mr-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <span>{post.created_at && formatDate(post.created_at)}</span>
                </div>
              </div>
            </header>

            {/* Post Content */}
            <div className="prose prose-lg max-w-none">
              <div className="text-gray-800 leading-relaxed whitespace-pre-wrap">
                {post.content}
              </div>
            </div>

            {/* Post Footer */}
            <footer className="mt-12 pt-8 border-t border-gray-200">
              <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                {post.updated_at && post.updated_at !== post.created_at && (
                  <div className="flex items-center">
                    <svg
                      className="w-4 h-4 mr-2"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                      />
                    </svg>
                    <span>Обновлено: {formatDate(post.updated_at)}</span>
                  </div>
                )}
              </div>
            </footer>
          </div>
        </article>
      </div>
    </div>
  )
}

export default PostDetail
